import React, { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Info, Save, Lock, RotateCcw, Settings } from "lucide-react";
import { PemexVinculoDialog } from "@/components/control/PemexVinculoDialog";
import { toast } from "sonner";
import { Slider } from "@/components/ui/slider";
import { api, formatApiErrorDetail } from "@/lib/api";
import { weekLabel } from "@/lib/semanas";

const clamp = (v, max) => (Number.isFinite(v) ? Math.max(0, max == null ? v : Math.min(max, v)) : 0);
const pick = (items, fs) => JSON.stringify((items || []).map((it) => ({ concepto_id: it.concepto_id, proveedor: it.proveedor, ...Object.fromEntries(fs.map((f) => [f, it[f]])) })));
const badge = (p) => `inline-flex px-3 py-1 rounded-full text-xs font-bold ${p === "PEMEX" ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`;
const inputCls = "h-9 rounded-lg border border-slate-200 bg-slate-50 px-2 text-sm text-right text-slate-900 tabular-nums focus:outline-none focus:ring-2 focus:ring-red-200";
const fleteCents = (it, fs) => fs.reduce((s, f) => s + Math.round((Number(it[f]) || 0) * 100), 0);
const mxn = (v) => `$${v.toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const IVA = [["8", "8%", 108], ["16", "16%", 116], ["incluido", "IVA incluido", 100]];
const netoCents = (it) => Math.round(netoU(it) / 1000);
const netoU = (it) => Math.round((Number(it.precio_sd) || 0) * 1e5) - Math.round((Number(it.descuento) || 0) * 1e5);
// Precio KG = (sin descuento − descuento) × factor de IVA, a centavos (respeta los 5 decimales de PEMEX).
const precioKg = (it) => Math.round(netoU(it) * (IVA.find((x) => x[0] === it.iva) || IVA[1])[2] / 100 / 1000) / 100;
const n5 = (v) => `$${Number(v).toLocaleString("es-MX", { minimumFractionDigits: 5, maximumFractionDigits: 5 })}`;
export const aplicarPemex = (it) => (it.pemex?.disponible ? conPrecio(it, { precio_sd: it.pemex.precio_sd, descuento: it.pemex.descuento }) : it);
const conPrecio = (it, patch) => {
  const n = { ...it, precio_sd: it.precio_sd ?? 0, descuento: it.descuento ?? 0, iva: it.iva ?? "16", ...patch };
  return { ...n, precio: precioKg(n) };
};

function FleteTotalRow({ items, fields }) {
  const missing = items.some((it) => it.pct_compra == null);
  // centavos × centésimas de % = unidades de 1e-6 MXN; redondeo final a centavos.
  const units = items.reduce((s, it) => s + fleteCents(it, fields) * Math.round(it.pct_compra * 100), 0);
  const pctSum = items.reduce((s, it) => s + Math.round((it.pct_compra ?? 0) * 100), 0) / 100;
  const allMissing = items.every((it) => it.pct_compra == null);
  return (
    <tfoot>
      <tr className="border-t-2 border-slate-300 bg-slate-50 font-bold text-slate-900" data-testid="flete-total-row">
        <td colSpan={3} className="px-3 py-3">Flete total</td>
        <td className="px-3 py-3 text-right tabular-nums whitespace-nowrap" data-testid="flete-total-pct">
          {allMissing ? <span className="text-xs font-normal italic text-slate-400">Sin configurar</span> : <span className={pctSum === 100 ? "" : "text-amber-700"}>{pctSum.toLocaleString("es-MX", { maximumFractionDigits: 2 })}%</span>}
        </td>
        <td colSpan={fields.length} />
        <td className="px-3 py-3 text-right tabular-nums whitespace-nowrap" data-testid="flete-total-ponderado">
          {missing ? <span className="text-xs font-normal italic text-slate-400">Sin configurar</span> : mxn(Math.round(units / 10000) / 100)}
        </td>
      </tr>
    </tfoot>
  );
}

function PcTotalRow({ items }) {
  // Igual que Flete total: centavos × centésimas de % y redondeo final a centavos.
  const units = items.reduce((s, it) => s + Math.round((Number(it.precio) || 0) * 100) * Math.round((Number(it.pct) || 0) * 100), 0);
  const pctSum = items.reduce((s, it) => s + Math.round((Number(it.pct) || 0) * 100), 0) / 100;
  const sinPrecio = items.every((it) => !Number(it.precio));
  return (
    <tfoot>
      <tr className="border-t-2 border-slate-300 bg-slate-50 font-bold text-slate-900" data-testid="compra-pc-total-row">
        <td colSpan={7} className="px-3 py-3">P.C. total</td>
        <td className="px-3 py-3 text-right tabular-nums whitespace-nowrap" data-testid="compra-pc-total">
          {sinPrecio ? <span className="text-xs font-normal italic text-slate-400">Sin configurar</span> : mxn(Math.round(units / 10000) / 100)}
        </td>
        <td className="px-3 py-3 pl-6 text-right tabular-nums whitespace-nowrap" data-testid="compra-pc-total-pct">
          <span className={pctSum === 100 ? "" : "text-amber-700"}>{pctSum.toLocaleString("es-MX", { maximumFractionDigits: 2 })}%</span>
        </td>
        <td />
      </tr>
    </tfoot>
  );
}

// isDefault: cero asignado por el sistema (no capturado); se limpia visualmente al recibir el foco.
function NumInput({ value, disabled, onChange, testid, max, money, w, isDefault }) {
  const fmt = (v) => (money ? Number(v).toFixed(2) : String(v));
  const [txt, setTxt] = useState(fmt(value));
  useEffect(() => { setTxt((t) => (t !== "" && Number(t) === value ? t : fmt(value))); }, [value]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="relative">
      {money && <span className="absolute left-2.5 top-2 text-sm text-slate-400">$</span>}
      <input type="number" min={0} max={max ?? undefined} step={0.01} value={txt} disabled={disabled} data-testid={testid}
        onKeyDown={(e) => { if (e.key === "-" || e.key === "e") e.preventDefault(); }}
        onChange={(e) => { setTxt(e.target.value); onChange(e.target.value === "" ? 0 : clamp(Number(e.target.value), max)); }}
        onFocus={() => { if (isDefault && !Number(value)) setTxt(""); }}
        onBlur={() => setTxt(fmt(value))}
        className={`${inputCls} ${w || (money ? "w-32 pl-6" : "w-24")} ${money ? "pl-6" : ""}`} />
    </div>
  );
}

function ProveedorCell({ it }) {
  return <span data-testid={`compra-proveedor-${it.concepto_id}`} className={badge(it.proveedor)}>{it.proveedor}</span>;
}

function ConceptoRow({ it, idx, mode, canEdit, canLink, onLink, onChange: emit }) {
  const isPct = mode.fields[0] === "pct";
  const pem = it.pemex;
  const lock = !!pem;
  const caps = it.capturados || [];
  const def = (f) => !caps.includes(f) && !Number(it[f]);
  const onChange = (n) => emit({ ...n, capturados: [...new Set([...caps, ...["pct", "precio_sd", "descuento", ...mode.fields].filter((f) => Number(n[f] ?? 0) !== Number(it[f] ?? 0))])] });
  return (
    <tr className="border-t border-slate-100" data-testid={`compra-row-${it.concepto_id}`}>
      <td className="px-3 py-2 text-xs text-slate-400 tabular-nums w-10">{idx + 1}</td>
      <td className="px-3 py-2 w-40"><ProveedorCell it={it} mode={mode} canEdit={canEdit} onChange={onChange} /></td>
      <td className="px-3 py-2 font-semibold text-slate-800" data-testid={`compra-concepto-${it.concepto_id}`}>
        <div className="flex items-center gap-1.5">
          <span className="truncate">{it.concepto}</span>
        </div>
        {pem && <span className={`mt-0.5 inline-block rounded-full px-1.5 py-0.5 text-[10px] font-bold ${pem.disponible ? "bg-sky-50 text-sky-700" : "bg-amber-50 text-amber-700"}`} data-testid={`compra-pemex-badge-${it.concepto_id}`}
          title={pem.disponible ? `Circular ${pem.circular}${pem.heredado ? " (última anterior)" : ""}` : "Sin precio PEMEX disponible para esta semana"}>
          PEMEX · {pem.punto}{pem.disponible ? (pem.heredado ? " · heredado" : "") : " · sin precio"}</span>}
      </td>
      {isPct && (
        <>
          <td className="px-2 py-2">{lock ? <Oficial v={it.precio_sd} pem={pem} testid={`compra-precio-sd-${it.concepto_id}`} /> : <NumInput w="w-full" value={it.precio_sd ?? 0} isDefault={def("precio_sd")} money disabled={!canEdit} testid={`compra-precio-sd-${it.concepto_id}`} onChange={(v) => onChange(conPrecio(it, { precio_sd: v, descuento: Math.min(it.descuento ?? 0, v) }))} />}</td>
          <td className="px-2 py-2">{lock ? <Oficial v={it.descuento} pem={pem} testid={`compra-descuento-${it.concepto_id}`} /> : <NumInput w="w-full" value={it.descuento ?? 0} isDefault={def("descuento")} money max={it.precio_sd ?? 0} disabled={!canEdit} testid={`compra-descuento-${it.concepto_id}`} onChange={(v) => onChange(conPrecio(it, { descuento: v }))} />}</td>
          <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap text-slate-700" data-testid={`compra-precio-cd-${it.concepto_id}`}>{it.precio_sd == null ? "—" : lock ? n5(netoU(it) / 1e5) : mxn(netoCents(it) / 100)}</td>
          <td className="px-2 py-2">
            <select value={it.iva ?? "16"} disabled={!canEdit} onChange={(e) => onChange(conPrecio(it, { iva: e.target.value }))} data-testid={`compra-iva-${it.concepto_id}`}
              className="h-9 w-full rounded-lg border border-slate-200 bg-slate-50 px-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-red-200">
              {IVA.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </td>
          <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap font-bold text-slate-900" data-testid={`compra-precio-${it.concepto_id}`}>{mxn(Number(it.precio) || 0)}</td>
          <td className="px-3 py-2 pl-6">
            <div className="flex items-center gap-3">
              <Slider value={[it.pct]} min={0} max={100} step={1} disabled={!canEdit} className="flex-1"
                onValueChange={([v]) => onChange({ ...it, pct: Math.round(v) })} data-testid={`compra-slider-${it.concepto_id}`} />
              <NumInput w="w-20" value={it.pct} isDefault={def("pct")} max={100} disabled={!canEdit} testid={`compra-pct-${it.concepto_id}`} onChange={(v) => onChange({ ...it, pct: v })} />
            </div>
          </td>
          <td className="px-2 py-2 text-center">
            {canLink && <button type="button" onClick={() => onLink(it)} title="Configuración de precio PEMEX" data-testid={`compra-pemex-cfg-${it.concepto_id}`}
              className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-red-600 transition-colors"><Settings className="w-4 h-4" /></button>}
          </td>
        </>
      )}
      {!isPct && (
        <>
          <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap" data-testid={`flete-pct-${it.concepto_id}`}>
            {it.pct_compra == null ? <span className="text-xs text-slate-400 italic">Sin configurar</span> : <span className="font-semibold text-slate-700">{it.pct_compra}%</span>}
          </td>
          {mode.fields.map((f) => (
            <td key={f} className="px-2 py-2">
              <NumInput value={it[f]} isDefault={def(f)} money disabled={!canEdit} testid={`flete-${f}-${it.concepto_id}`} onChange={(v) => onChange({ ...it, [f]: v })} />
            </td>
          ))}
          <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap font-bold text-slate-900" data-testid={`flete-total-${it.concepto_id}`}>
            {mxn(fleteCents(it, mode.fields) / 100)}
          </td>
        </>
      )}
    </tr>
  );
}

function Oficial({ v, pem, testid }) {
  return (
    <div className={`h-9 flex items-center justify-end gap-1.5 rounded-lg border px-2 text-sm tabular-nums ${pem.disponible ? "border-sky-100 bg-sky-50/60 text-sky-900" : "border-amber-100 bg-amber-50/60 text-slate-500"}`}
      title={pem.disponible ? "Valor oficial PEMEX (no editable)" : "Sin precio PEMEX: se conserva el valor guardado"} data-testid={testid}>
      <Lock className="w-3 h-3 shrink-0 opacity-60" />{v == null ? "—" : n5(v)}
    </div>
  );
}

function Origen({ data, mode }) {
  if (!data || data.estado === "guardada" || data.bloqueada) return null;
  return (
    <div className="flex items-start gap-2 text-xs text-slate-600 bg-amber-50 border border-amber-100 rounded-xl px-3 py-2" data-testid={`compra-origin-${data.planta}`}>
      <Info className="w-4 h-4 text-amber-600 shrink-0" />
      {data.estado === "heredada"
        ? `Esta semana aún no tiene configuración propia. Se muestran los valores de la semana ${data.origen_week} de ${data.origen_year}; ${mode.propaga ? "al guardar se registran para esta semana y las siguientes del año" : "al guardar se crean para esta semana sin modificar la anterior"}.`
        : `Sin configuración anterior: ${mode.initText}. Guarda para registrar esta semana.`}
    </div>
  );
}

export function CompraEditor({ mode, planta, year, week, structure, canEdit: canEditRole, onDirtyChange }) {
  const qc = useQueryClient();
  const [items, setItems] = useState(null);
  const [saving, setSaving] = useState(false);
  const f = [...(mode.save || mode.fields), "capturados"];
  const { data, isLoading } = useQuery({
    queryKey: [mode.qk, "config", planta, year, week],
    queryFn: async () => (await api.get(`/control-semanal/${mode.base}/config`, { params: { planta, year, week, structure } })).data,
  });
  useEffect(() => { if (data) { setItems(data.items.map(aplicarPemex)); } }, [data]);
  const [link, setLink] = useState(null);
  const bloqueada = !!data?.bloqueada;
  const canEdit = canEditRole && !bloqueada;
  const changed = !!data && !!items && pick(items, f) !== pick(data.items, f);
  const dirty = changed || (!!data && data.estado !== "guardada");
  const total = mode.sum100 && items ? items.reduce((s, it) => s + Math.round((Number(it.pct) || 0) * 100), 0) / 100 : null;
  const sumOk = !mode.sum100 || total === 100;
  useEffect(() => { onDirtyChange(planta, changed || saving); }, [changed, saving, planta, onDirtyChange]);
  useEffect(() => () => { onDirtyChange(planta, false); }, [planta, onDirtyChange]);
  const save = async () => {
    setSaving(true);
    try {
      const res = await api.put(`/control-semanal/${mode.base}/config`, { planta, structure, year, week, items: JSON.parse(pick(items, f)) });
      toast.success(res.data?.hasta_semana
        ? `${planta}: configuración guardada en la semana ${week} y replicada completa hasta la semana ${res.data.hasta_semana} de ${year} (${res.data.semanas_actualizadas} semanas).`
        : `${planta}: configuración guardada (semana ${week} de ${year}).`);
      qc.invalidateQueries({ queryKey: [mode.qk] });
      qc.invalidateQueries({ queryKey: ["fletes", "config"] });
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "No se pudo guardar.");
    } finally { setSaving(false); }
  };
  if (isLoading || !items) return <div className="py-6 pl-10"><Loader2 className="w-5 h-5 text-red-600 animate-spin" /></div>;
  return (
    <div className="px-6 py-4 space-y-3" data-testid={`compra-editor-${planta}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-slate-600"><b className="text-slate-900">{planta}</b> · {year} · {weekLabel(year, week)}</p>
        {bloqueada
          ? <span className="flex items-center gap-1.5 text-[11px] font-semibold text-slate-500" data-testid={`compra-bloqueada-${planta}`}><Lock className="w-3.5 h-3.5" />Solo lectura — Periodo histórico. Se requiere permiso de administrador para modificar</span>
          : changed && <span className="text-[11px] font-semibold text-amber-700" data-testid={`compra-unsaved-${planta}`}>Cambios sin guardar</span>}
      </div>
      <Origen data={data} mode={mode} />
      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
        <table className={`w-full text-sm ${mode.cols ? "table-fixed min-w-[1180px]" : mode.fields.length > 1 ? "min-w-[1250px]" : "min-w-[720px]"}`} data-testid={`compra-config-table-${planta}`}>
          {mode.cols && <colgroup>{["w-10", "w-28", "w-36", ...mode.cols.map((c) => c[1]), "w-12"].map((w, i) => <col key={i} className={w} />)}</colgroup>}
          <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
            <tr><th className="text-left px-3 py-2">#</th><th className="text-left px-3 py-2">Proveedor</th><th className="text-left px-3 py-2">Concepto</th>{mode.headers.map((h, i) => <th key={h} className={`px-3 py-2 whitespace-nowrap ${mode.cols ? mode.cols[i][2] : (mode.fields[0] !== "pct" && i === 0) || i === mode.headers.length - 1 ? "text-right" : "text-left"}`}>{h}</th>)}{mode.cols && <th className="px-2 py-2" aria-label="Configuración" />}</tr>
          </thead>
          <tbody>
            {items.map((it, i) => (
              <ConceptoRow key={it.concepto_id} it={it} idx={i} mode={mode} canEdit={canEdit} canLink={canEditRole} onLink={setLink}
                onChange={(n) => setItems((cur) => cur.map((x) => (x.concepto_id === n.concepto_id ? n : x)))} />
            ))}
          </tbody>
          {mode.fields[0] !== "pct" ? <FleteTotalRow items={items} fields={mode.fields} /> : <PcTotalRow items={items} />}
        </table>
      </div>
      {canEdit && (
        <div className="flex flex-wrap items-center justify-end gap-4">
          {mode.sum100 && (
            <span data-testid={`compra-total-${planta}`} className={`text-sm font-semibold ${sumOk ? "text-emerald-700" : "text-amber-700"}`}>
              Total: {total}% / 100% — {sumOk ? "Completo" : total < 100 ? `Faltante: ${Math.round((100 - total) * 100) / 100}%` : `Excedente: ${Math.round((total - 100) * 100) / 100}%`}
              {!sumOk && <span className="block text-[11px] font-normal">La suma de porcentajes debe ser exactamente 100% para guardar.</span>}
            </span>
          )}
          {changed && <button onClick={() => setItems(data.items.map(aplicarPemex))} disabled={saving} data-testid={`compra-discard-${planta}`} className="h-9 px-4 rounded-lg border border-slate-200 bg-white text-sm font-semibold text-slate-600 hover:bg-slate-100 disabled:opacity-50 flex items-center gap-2">
            <RotateCcw className="w-4 h-4" />Descartar cambios
          </button>}
          <button onClick={save} disabled={saving || !dirty || !sumOk} data-testid={`compra-save-${planta}`} className="h-9 px-4 rounded-lg bg-red-600 text-white text-sm font-semibold hover:bg-red-700 disabled:opacity-50 flex items-center gap-2">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}{saving && mode.propaga ? "Guardando y propagando..." : "Guardar cambios"}
          </button>
        </div>
      )}
      {link && <PemexVinculoDialog planta={planta} structure={structure} it={link} onClose={() => setLink(null)} />}
    </div>
  );
}
